/**
 * Theme-safe resolution of the absolute colours authored in BI widget configuration.
 *
 * A colour written into a widget config is fixed, but the surface and text colours around it come
 * from the active theme. A pastel background picked while looking at the light theme turns
 * light-on-light in dark mode, and a dark text colour picked the same way becomes dark-on-dark.
 * These helpers keep an authored colour exactly as written whenever it still reads against the
 * active theme, and adapt it - in the same hue - only when it does not.
 *
 * No colour text is parsed by hand: the browser's own CSS parser validates and normalises whatever
 * the author wrote by round-tripping it through a detached element, and ColorUtils (over Quasar's
 * colour utils) turns the result into channels.
 */
import {colors, type colorsRgba} from "quasar";
import ColorUtils from "@/utils/common/ColorUtils.ts";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";

/** Opacity a background is repainted with when it cannot carry the theme's text colour. */
const BG_TINT_ALPHA_PERCENT = 22;
/** A background only stays opaque while the theme's text clears this WCAG contrast ratio on it. */
const MIN_BG_TEXT_CONTRAST = 3;
/** An authored text colour is nudged until it clears this ratio against the surface behind it. */
const MIN_TEXT_SURFACE_CONTRAST = 4.5;
const TEXT_ADJUST_STEP_PERCENT = 10;
const MAX_TEXT_ADJUST_STEPS = 9;

// Fall back to the ThemeDataStore defaults when a theme variable has not been published yet.
const FALLBACK_TEXT = {dark: '#FFFFFF', light: '#1F2937'};
const FALLBACK_SURFACE = {dark: '#1E1E1E', light: '#FFFFFF'};

let colorProbe: HTMLElement | undefined;
const parsedColors = new Map<string, colorsRgba | null>();

/** Parsed channels of any CSS colour, or null when it has no fixed colour we can reason about. */
export function parseColor(color: string | undefined | null): colorsRgba | null {
  if (!color) return null;
  let rgb = parsedColors.get(color);
  if (rgb === undefined) {
    rgb = resolveColor(color);
    parsedColors.set(color, rgb);
  }
  return rgb;
}

function resolveColor(color: string): colorsRgba | null {
  colorProbe ??= document.createElement('div');
  colorProbe.style.color = '';
  colorProbe.style.color = color;
  // An invalid value leaves the property empty. Hex, rgb(), rgba() and hsl() all come back
  // serialised as rgb()/rgba() (so alpha is always on the 0-1 scale), while a colour keyword stays
  // as written and ColorUtils' own name table resolves it. Everything else - var(...),
  // color-mix(...), transparent - has no fixed colour to reason about and is left to the author.
  const normalized = colorProbe.style.color.toLowerCase();
  if (normalized.startsWith('rgb')) return ColorUtils.toRGB(normalized);
  if ((ColorUtils.names as Record<string, string>)[normalized]) return ColorUtils.toRGB(normalized);
  return null;
}

/** WCAG contrast ratio, off Quasar's relative-luminance implementation. */
export function contrastRatio(a: colorsRgba, b: colorsRgba): number {
  const la = colors.luminosity(a), lb = colors.luminosity(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

function isTranslucent(rgb: colorsRgba): boolean {
  return rgb.a !== undefined && rgb.a < 1;
}

function readThemeColor(cssVar: string, fallback: { dark: string; light: string }): colorsRgba | null {
  const published = document.body.style.getPropertyValue(cssVar)
    || document.documentElement.style.getPropertyValue(cssVar);
  return parseColor(published.trim())
    ?? parseColor(useNamaFlagsStore().darkMode ? fallback.dark : fallback.light);
}

/** The colour AG Grid paints cell text with, as published by AGGridUtils.updateThemeVars(). */
export function gridTextColor(): colorsRgba | null {
  return readThemeColor('--nama-ag-grid-cell-text-color', FALLBACK_TEXT);
}

/** The theme's ordinary text colour, as published by ThemeDataStore. */
export function themeTextColor(): colorsRgba | null {
  const dark = useNamaFlagsStore().darkMode;
  return readThemeColor(dark ? '--nama-font-input-dark' : '--nama-font-input-light', FALLBACK_TEXT);
}

/** The grid's own background, i.e. what a translucent cell background composites over. */
export function gridSurfaceColor(): colorsRgba | null {
  return readThemeColor('--nama-ag-grid-background-color', FALLBACK_SURFACE);
}

/** The background of a dashboard widget's card surface. */
export function widgetSurfaceColor(): colorsRgba | null {
  const dark = useNamaFlagsStore().darkMode;
  return readThemeColor(dark ? '--nama-background-bright-dark' : '--nama-background-bright-light', FALLBACK_SURFACE);
}

/**
 * An authored background, kept as written while the theme's text colour still contrasts with it,
 * and repainted as a low-alpha tint of the same hue when it does not: composited over the surface,
 * a pastel becomes a dark band of the same hue in dark mode, and a dark fill becomes a pale one in
 * light mode, while the text keeps the theme colour it needs.
 *
 * Passing the authored text colour in `pairedWith` hands the decision back to the author: a style
 * that sets both a background and a text colour is applied verbatim, and owns its own contrast.
 */
export function themeSafeBackground(bg: string | undefined, pairedWith?: string,
                                    text: colorsRgba | null = gridTextColor()): string | undefined {
  if (!bg) return undefined;
  if (pairedWith) return bg;
  const rgb = parseColor(bg);
  // Unreadable to us, or already translucent - the author has chosen the strength themselves.
  if (!rgb || isTranslucent(rgb)) return bg;
  if (text && contrastRatio(rgb, text) >= MIN_BG_TEXT_CONTRAST) return bg;
  return colors.rgbToHex({r: rgb.r, g: rgb.g, b: rgb.b, a: BG_TINT_ALPHA_PERCENT});
}

/**
 * An authored text colour, kept as written while it still reads on the surface behind it, and
 * otherwise walked towards the theme - lighter on a dark surface, darker on a light one - in steps
 * that keep its hue, stopping as soon as it clears the contrast threshold.
 */
export function themeSafeForeground(color: string | undefined,
                                    surface: colorsRgba | null = widgetSurfaceColor()): string | undefined {
  if (!color) return undefined;
  const rgb = parseColor(color);
  if (!rgb || isTranslucent(rgb) || !surface) return color;
  if (contrastRatio(rgb, surface) >= MIN_TEXT_SURFACE_CONTRAST) return color;
  const towardsLight = colors.luminosity(surface) < 0.5;
  let candidate = colors.rgbToHex({r: rgb.r, g: rgb.g, b: rgb.b});
  let best = candidate, bestRatio = contrastRatio(rgb, surface);
  for (let step = 0; step < MAX_TEXT_ADJUST_STEPS; step++) {
    candidate = colors.lighten(candidate, towardsLight ? TEXT_ADJUST_STEP_PERCENT : -TEXT_ADJUST_STEP_PERCENT);
    const ratio = contrastRatio(colors.textToRgb(candidate), surface);
    if (ratio >= MIN_TEXT_SURFACE_CONTRAST) return candidate;
    if (ratio > bestRatio) {
      best = candidate;
      bestRatio = ratio;
    }
  }
  return best;
}
